import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { navLinks, companyInfo } from '@/mocks/home';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleNav = (href: string) => {
    setMobileOpen(false);
    if (href.startsWith('#')) {
      const el = document.querySelector(href);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(href);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-blue-600/95 backdrop-blur-md border-b border-blue-700/30'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="/" className="flex flex-col items-start">
            <span
              className={`text-xl md:text-2xl font-heading font-bold tracking-tight transition-colors duration-300 ${
                scrolled ? 'text-white' : 'text-white'
              }`}
            >
              {companyInfo.name}
            </span>
            <span
              className={`text-[10px] md:text-xs font-label transition-colors duration-300 ${
                scrolled ? 'text-blue-100' : 'text-white/70'
              }`}
            >
              {companyInfo.loanRegNumber}
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNav(link.href)}
                className={`text-sm font-label font-medium transition-colors duration-300 hover:opacity-80 whitespace-nowrap ${
                  scrolled ? 'text-white/90 hover:text-white' : 'text-white/90 hover:text-white'
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <a
            href={`tel:${companyInfo.phone}`}
            className={`hidden md:flex items-center gap-2 px-4 py-2 rounded-full font-label font-semibold text-sm transition-all duration-300 whitespace-nowrap ${
              scrolled
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
            }`}
          >
            <i className="ri-phone-line w-4 h-4 flex items-center justify-center" />
            {companyInfo.phone}
          </a>

          <button
            className="lg:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="메뉴 열기"
          >
            <i
              className={`ri-menu-line w-6 h-6 flex items-center justify-center text-xl text-white`}
            />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-blue-600 border-t border-blue-700/30 shadow-lg">
          <nav className="flex flex-col py-4">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNav(link.href)}
                className="px-6 py-3 text-left text-sm font-label font-medium text-white/90 hover:bg-blue-700 hover:text-white transition-colors"
              >
                {link.label}
              </button>
            ))}
            <a
              href={`tel:${companyInfo.phone}`}
              className="mx-6 mt-3 flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-blue-500 text-white font-label font-semibold text-sm hover:bg-blue-600 transition-colors"
            >
              <i className="ri-phone-line w-4 h-4 flex items-center justify-center" />
              {companyInfo.phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}